import React, { useState, useEffect, useRef, useCallback } from 'react';

const SPLASH_VIDEO_URL = 'https://customer-assets.emergentagent.com/job_a750dd9f-0994-4419-991b-3e10a88192b9/artifacts/j4ibeoxf_Short%20Spalsh.mp4';

// Helper to request fullscreen
const requestFullscreen = async () => {
  const doc = document.documentElement;
  try {
    if (doc.requestFullscreen) {
      await doc.requestFullscreen({ navigationUI: 'hide' });
    } else if (doc.webkitRequestFullscreen) {
      await doc.webkitRequestFullscreen();
    } else if (doc.mozRequestFullScreen) {
      await doc.mozRequestFullScreen();
    } else if (doc.msRequestFullscreen) {
      await doc.msRequestFullscreen();
    }
  } catch (e) {
    // Silently fail - fullscreen is optional
  }
};

export function SplashScreen({ onComplete }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const videoRef = useRef(null);
  const hasCompletedRef = useRef(false);

  const handleComplete = useCallback((triggerFullscreen = false) => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    
    // Request fullscreen on user interaction (skip button tap)
    if (triggerFullscreen) {
      requestFullscreen();
    }
    
    setIsFading(true);
    // Wait for fade animation to complete
    setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 500);
  }, [onComplete]);

  useEffect(() => {
    // Fallback timeout in case video doesn't load or play
    const fallbackTimer = setTimeout(() => {
      handleComplete(false); // Don't trigger fullscreen on timeout (no user gesture)
    }, 8000); // 8 second max wait

    return () => clearTimeout(fallbackTimer);
  }, [handleComplete]);

  const handleVideoEnd = () => {
    handleComplete(false); // Video end is not a user gesture
  };

  const handleVideoError = () => {
    // If video fails to load, complete immediately
    handleComplete(false);
  };

  const handleCanPlay = () => {
    // Try to play the video when it's ready
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // If autoplay fails, complete after a short delay
        setTimeout(handleComplete, 2000);
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black transition-opacity duration-500 cursor-pointer ${
        isFading ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="splash-screen"
      onClick={() => handleComplete(true)}
    >
      <video
        ref={videoRef}
        src={SPLASH_VIDEO_URL}
        className="w-full h-full object-contain pointer-events-none"
        autoPlay
        muted
        playsInline
        onEnded={handleVideoEnd}
        onError={handleVideoError}
        onCanPlay={handleCanPlay}
      />
      
      {/* Skip functionality preserved but hidden - tap anywhere still works */}
      {/* 
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-2 pointer-events-none">
        <span className="text-white/50 text-xs">Tap anywhere to skip</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleComplete(true);
          }}
          className="px-4 py-2 text-white/70 hover:text-white text-sm font-medium transition-colors pointer-events-auto"
          data-testid="skip-splash-btn"
        >
          Skip
        </button>
      </div>
      */}
    </div>
  );
}

export default SplashScreen;
